import { useCallback, useMemo } from 'react'
import type * as React from 'react'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LandmarkIcon from 'LuxKit/Icons/custom/LandmarkIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import Label from 'LuxKit/Label'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import switchFunc from 'lib/function/switchFunc'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import { escapeRegex } from 'lib/string/regexUtils'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import SearchMenuTourItem from './SearchMenuTourItem'
import Image from 'components/Common/Image/Image'
import styled from 'styled-components'
import { rem } from 'polished'
import { getOfferIdFromSearchItem, getSearchMenuListItemDiscount } from 'components/Search/utils'
import { getSearchItemExperienceId, isExperienceSearchItem } from 'lib/search/searchItem'
import clsx from 'clsx'
import Caption from 'LuxKit/Typography/Caption'
import useOffer from 'hooks/Offers/useOffer'
import useExperience from 'hooks/Experiences/useExperience'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { isBedbankOffer, isExperienceOffer, isHotel, isLEHotel } from 'lib/offer/offerTypes'
import LineGlobeAltIcon from '../../../../../LuxKit/Icons/line/LineGlobeAltIcon'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import formatStaticAppText from 'lib/format/formatStaticAppText'
import { SEARCH_MENU_LIST_ITEM_CLASS_NAME } from './searchMenuListItemConstants'

export { SEARCH_MENU_LIST_ITEM_CLASS_NAME } from './searchMenuListItemConstants'

const ImageBlock = styled.div`
  flex-shrink: 0;
  width: ${rem(48)};
  height: ${rem(48)};
`

const IconBlock = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  padding: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledListItem = styled(ClickableListItem)`
  &.horizontal {
    width: ${rem(325)};
  }
`

interface Props {
  /** highlights the portion of word that matches the search phrase */
  searchPhrase?: string
  searchItem: App.SearchItem
  onClick?: (searchItem: App.SearchItem, mouseEvent?: React.MouseEvent<HTMLElement>) => void
  ['data-testid']?: string
  selected?: boolean
  imageId?: string
  horizontal?: boolean
  offerId?: string
  searchVertical?: App.SearchPlaceVertical
  useImage?: boolean
  className?: string
  ref?: React.RefObject<HTMLElement | null>
}

type SearchItemTypeIndicator = 'city' | 'state' | 'province' | 'landmark' | 'hotel' | 'anywhere'

const searchItemPropertiesSwitch = switchFunc({
  city: { Icon: LineMapMarkerIcon, typeIndicator: 'city' as const },
  state: { Icon: LineMapMarkerIcon, typeIndicator: 'state' as const },
  province: { Icon: LineMapMarkerIcon, typeIndicator: 'province' as const },
  landmark: { Icon: LandmarkIcon, typeIndicator: 'landmark' as const },
  property: { Icon: LineBedIcon, typeIndicator: 'hotel' as const },
  experience: { Icon: LineCompassIcon, typeIndicator: null },
  destination: { Icon: LineMapMarkerIcon, typeIndicator: null },
  airport: { Icon: LinePlaneFlyIcon, typeIndicator: null },
  anywhere: { Icon: LineGlobeAltIcon, typeIndicator: 'anywhere' as const },
}, { Icon: LineMapMarkerIcon, typeIndicator: null })

function SearchMenuListItem(props: Props) {
  const {
    searchItem,
    onClick,
    searchPhrase,
    selected = false,
    horizontal = false,
    searchVertical,
    ref,
  } = props

  const intl = useIntl()
  const useImage = searchVertical === 'tour' || props.useImage
  const isExperienceItem = isExperienceSearchItem(searchItem)
  const typeaheadImageId = props.imageId ||
    (searchItem.searchType === 'searchItemV3' ? searchItem.display.imageId : searchItem.imageId) ||
    searchItem.format.imageId
  const experienceId = isExperienceItem ? getSearchItemExperienceId(searchItem) : undefined

  const [offer] = useOffer(isExperienceItem ? undefined : getOfferIdFromSearchItem(searchItem))
  const [experience] = useExperience(experienceId ?? '', {
    disabled: !experienceId || !!typeaheadImageId,
  })

  const {
    Icon,
    subtitle,
    title,
    offerType,
    offerDiscount,
    isBedbankPromoOffer,
    hasTactical,
    isAnExperienceOffer,
  } = useMemo(() => {
    let iconToSelect: string | undefined
    if (searchItem.searchType === 'searchItemV3') {
      // For V3 items, use display.searchType directly
      iconToSelect = searchItem.display.searchType
    } else if (searchItem.searchType === 'destination') {
      // For V1 destination items, use destinationType (city, state, province, etc.)
      iconToSelect = searchItem.destinationType
    } else {
      // For other V1 items, use searchType directly
      iconToSelect = searchItem.searchType
    }

    if (searchItem.format.mainText === 'Anywhere') {
      iconToSelect = 'anywhere'
    }

    const properties = searchItemPropertiesSwitch(iconToSelect)

    const { mainText, secondaryText } = searchItem.format
    const { Icon, typeIndicator } = properties

    const subtitleItems: Array<string> = []

    if (typeIndicator) {
      const typeIndicatorMessages: Record<SearchItemTypeIndicator, string> = {
        city: intl.formatMessage({
          id: '-5ZD6kzVc428xXaZNOID',
          defaultMessage: 'City',
          description: 'Search item type indicator for city in the search and discovery interface',
        }),
        state: intl.formatMessage({
          id: '6patfMlXEDDV4UF4H6C1',
          defaultMessage: 'State',
          description: 'Search item type indicator for state in the search and discovery interface',
        }),
        province: intl.formatMessage({
          id: 'Wa6gSWOUOcMEjHFD3CBc',
          defaultMessage: 'Province',
          description: 'Search item type indicator for province in the search and discovery interface',
        }),
        landmark: intl.formatMessage({
          id: 'nVIr7DWAKmbFRiT7qiHO',
          defaultMessage: 'Landmark',
          description: 'Search item type indicator for landmark in the search and discovery interface',
        }),
        hotel: intl.formatMessage(productLabels.hotel),
        anywhere: intl.formatMessage({
          id: 'yfJ16X0bAoTTvBE2WW21',
          defaultMessage: 'Anywhere',
          description: 'Search item type indicator for anywhere in the search and discovery interface',
        }),
      }
      subtitleItems.push(typeIndicatorMessages[typeIndicator])
    }

    if (
      secondaryText &&
      mainText.localeCompare(secondaryText, 'en', { sensitivity: 'base' }) !== 0
    ) {
      subtitleItems.push(secondaryText)
    }
    const offerDiscount = getSearchMenuListItemDiscount(searchItem, offer)

    const isProperty =
      (searchItem.searchType === 'searchItemV3' && searchItem.display.searchType === 'property') ||
      searchItem.searchType === 'property'

    const sep = intl.formatMessage(middleDotSeparatorMessage)
    return {
      Icon,
      subtitle: subtitleItems.join(` ${sep} `),
      title: formatStaticAppText(intl, mainText),
      offerType: isProperty ? offer?.type : null,
      offerDiscount,
      isBedbankPromoOffer: isProperty && isBedbankOffer(offer) ? offer.promotions.length > 0 : false,
      hasTactical: isProperty && isLEHotel(offer) ? offer.hasTactical : false,
      isAnExperienceOffer: isExperienceOffer(offer),
    }
  }, [offer, searchItem, intl])

  const startIcon = useMemo(() => {
    let imageId = typeaheadImageId

    if (offer && isHotel(offer)) {
      imageId = offer.image.id ?? imageId
    }

    if (!imageId && experience?.images[0]?.id) {
      imageId = experience.images[0].id
    }

    if (!useImage) {
      return <Icon />
    }

    return (
      <>
        {!imageId && <IconBlock>
          <Icon />
        </IconBlock>}
        {imageId && <ImageBlock>
          <Image
            fit="center"
            width={68}
            height={92}
            dpr={2}
            id={imageId}
          />
        </ImageBlock>}
      </>
    )
  }, [offer, experience, Icon, typeaheadImageId, useImage])

  const discountLabel = useMemo(() => {
    const limitedTimeOfferText = intl.formatMessage({
      id: '51ODhDEcQI-f7MqwAysz',
      defaultMessage: 'Limited time offer',
      description: 'Label indicating a limited time promotional offer in the search interface',
    })

    if (hasTactical) {
      return (
        <LabelGroup>
          <Label variant="highlight-primary-dark" kind="plain">{limitedTimeOfferText}</Label>
          {!!offerDiscount && offerDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={offerDiscount}
          />}
        </LabelGroup>
      )
    }
    else if (offerType === 'hotel' || offerType === 'tactical_ao_hotel' || isBedbankPromoOffer) {
      return (
        <LabelGroup>
          { (offerType === 'hotel' || isBedbankPromoOffer) && <Label variant="highlight-primary-dark" kind="plain">{limitedTimeOfferText}</Label>}
          {!!offerDiscount && offerDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={offerDiscount}
          />}
        </LabelGroup>
      )
    } else if (isAnExperienceOffer) {
      return (
        <LabelGroup>
          {!!offerDiscount && offerDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={offerDiscount}
          />}
        </LabelGroup>
      )
    }
  }, [offerType, offerDiscount, isBedbankPromoOffer, hasTactical, intl, isAnExperienceOffer])

  const subTitleLabel = useMemo(() => {
    if (subtitle && useImage) {
      return (
        <Caption variant="medium" lineClamp={1}>
          {subtitle}
        </Caption>
      )
    }

    return subtitle
  }, [subtitle, useImage])

  const handleClick = useCallback((mouseEvent: React.MouseEvent<HTMLElement>) => {
    onClick?.(searchItem, mouseEvent)
  }, [onClick, searchItem])

  const label = useMemo(() => {
    if (!searchPhrase) {
      return title
    }

    const search = new RegExp(`(${escapeRegex(searchPhrase)})`, 'ig')
    const bits = title.split(search)

    return <>
      {bits.map((textBit, index) => textBit.toLowerCase() === searchPhrase.toLowerCase() ? <b key={index}>{textBit}</b> : textBit, '')}
    </>
  }, [searchPhrase, title])

  if (searchItem.searchType === 'tour') {
    return <SearchMenuTourItem
      searchItem={searchItem}
      searchPhrase={searchPhrase}
      selected={selected}
      data-testid={props['data-testid']}
      offerId={props.offerId}
    />
  }

  return <StyledListItem
    ref={ref}
    selected={selected}
    className={`${SEARCH_MENU_LIST_ITEM_CLASS_NAME} ${clsx({ horizontal, useImage })} ${props.className}`}
    startIcon={startIcon}
    title={label}
    subtitle={
      <VerticalSpacer gap={4}>
        {subTitleLabel}
        {discountLabel}
      </VerticalSpacer>
    }
    onClick={handleClick}
    titleLineClamp={(horizontal && useImage) ? 1 : 2}
    data-testid={props['data-testid']}
  />
}

export default SearchMenuListItem
